import type { Rng } from './prng';
import type { Corner, TrackFeatures } from './types';

/**
 * The day's hills. Most circuits are flat or gently rolling; a few are hilly;
 * rarely one gets a Spa-style plunge: down into a compression at a fast
 * corner, then a steep climb over a crest onto high ground (think Eau Rouge
 * and Raidillon).
 *
 * The profile is a sum of smooth, local polynomial pieces placed away from the
 * start/finish, so the main straight (gantry, grandstand, spawn) is always
 * dead flat and the lap closes on itself. Deterministic: plain arithmetic
 * only, like the rest of track/.
 */
export type TerrainKind = 'flat' | 'rolling' | 'hilly' | 'spa';

export interface ElevationInput {
  n: number;
  length: number;
  /** Arc length of each sample; sample 0 is the start line. */
  s: number[];
  corners: Corner[];
  features: TrackFeatures;
  /** Stretches (centre, half-length, metres of arc) no hill may touch: the underpass and its bridge. */
  reserved?: { at: number; half: number }[];
}

export interface Elevation {
  kind: TerrainKind;
  /** Height of the centreline at each sample, metres. */
  elev: number[];
}

/** Flat before (over the spawn run-up) and after the line (gantry, grandstand), metres. */
const FLAT_BEFORE = 110;
const FLAT_AFTER = 95;
/** Steepest grade allowed, per kind. */
const MAX_GRADE: Record<TerrainKind, number> = { flat: 0, rolling: 0.05, hilly: 0.08, spa: 0.15 };

/** C2 bump: 1 at x = 0, 0 (with zero slope and curvature) at |x| >= 1. */
function bump(x: number): number {
  if (x <= -1 || x >= 1) return 0;
  const u = 1 - x * x;
  return u * u * u;
}

/** C2 step from 0 (x <= 0) to 1 (x >= 1). */
function smootherstep(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  return x * x * x * (x * (x * 6 - 15) + 10);
}

interface Hill {
  at: number;
  height: number;
  /** Half-width, metres. */
  w: number;
}

/**
 * The Spa section, relative to the dip at `at`: ease down `drop` metres over
 * `ease`, climb `drop + rise` over `climb` (the steepest part, about 14%),
 * hold the high ground for `top`, then ease back to level over `back`.
 */
interface Spa {
  at: number;
  drop: number;
  rise: number;
  ease: number;
  climb: number;
  top: number;
  back: number;
}

function spaHeight(p: Spa, s: number): number {
  const u = s - p.at;
  if (u <= -p.ease) return 0;
  if (u < 0) return -p.drop * smootherstep((u + p.ease) / p.ease);
  if (u < p.climb) return -p.drop + (p.drop + p.rise) * smootherstep(u / p.climb);
  if (u < p.climb + p.top) return p.rise;
  if (u < p.climb + p.top + p.back) return p.rise * (1 - smootherstep((u - p.climb - p.top) / p.back));
  return 0;
}

/** Steepest slope of smootherstep (at its midpoint), per unit of height over length. */
const STEEPEST = 1.875;

export function elevationProfile(t: ElevationInput, rng: Rng): Elevation {
  const n = t.n;
  const L = t.length;
  // The stretch that may rise and fall: after the line's flat zone, before the next one.
  const lo = FLAT_AFTER;
  const hi = L - FLAT_BEFORE;
  const flat: Elevation = { kind: 'flat', elev: new Array<number>(n).fill(0) };
  const r = rng.next();
  let kind: TerrainKind = r < 0.44 ? 'flat' : r < 0.74 ? 'rolling' : r < 0.86 ? 'hilly' : 'spa';
  if (kind === 'flat' || hi - lo < 400) return flat;
  // One drama a day: a crossover day gets no Spa plunge.
  const reserved = t.reserved ?? [];
  const clash = (at: number, half: number): boolean => reserved.some((q) => arcGap(at, q.at, L) < half + q.half);
  if (kind === 'spa' && reserved.length) kind = 'hilly';

  let spa: Spa | null = null;
  if (kind === 'spa') {
    const drop = rng.range(6, 9);
    const rise = rng.range(8, 12);
    const p: Spa = {
      at: 0,
      drop,
      rise,
      ease: (STEEPEST * drop) / 0.13,
      climb: (STEEPEST * (drop + rise)) / 0.142,
      top: rng.range(30, 70),
      back: (STEEPEST * rise) / 0.1,
    };
    // The plunge bottoms out at a fast corner (a kink, fast esses or the sweeper), with room around it.
    const need = p.climb + p.top + p.back;
    const fits = t.corners.filter((c) => {
      if (c.minRadius < 40 || Math.abs(c.angle) < 0.2 || Math.abs(c.angle) > 1.8) return false;
      const at = t.s[c.apex];
      return at - p.ease >= lo && at + need <= hi;
    });
    if (fits.length === 0) kind = 'hilly';
    else spa = { ...p, at: t.s[fits[rng.int(0, fits.length - 1)].apex] };
  }

  // Rolling ground (or the rest of a Spa day): a few hills and dips away from the line.
  const hills: Hill[] = [];
  const busyFrom = spa ? spa.at - spa.ease : 0;
  const busyTo = spa ? spa.at + spa.climb + spa.top + spa.back : 0;
  const count = kind === 'rolling' ? rng.int(2, 3) : kind === 'hilly' ? rng.int(3, 4) : rng.int(0, 1);
  const hMin = kind === 'hilly' ? 4 : 2;
  const hMax = kind === 'hilly' ? 10 : 5;
  const wMin = kind === 'hilly' ? 200 : 180;
  const wMax = kind === 'hilly' ? 340 : 380;
  for (let k = 0, tries = 0; k < count && tries < 40; tries++) {
    const w = rng.range(wMin, wMax);
    if (hi - lo < 2 * w) continue;
    const at = rng.range(lo + w, hi - w);
    if (spa && at + w > busyFrom && at - w < busyTo) continue;
    if (reserved.length && clash(at, w)) continue;
    hills.push({ at, height: (rng.chance(0.5) ? 1 : -1) * rng.range(hMin, hMax), w });
    k++;
  }
  if (hills.length === 0 && !spa) return flat;

  const elevAt = (s: number, hillScale: number): number => {
    let h = spa ? spaHeight(spa, s) : 0;
    for (const p of hills) h += hillScale * p.height * bump((s - p.at) / p.w);
    return h;
  };
  // Tame the hills if overlapping ones got steeper than the day allows.
  const ds = L / n;
  let scale = 1;
  for (let pass = 0; pass < 6; pass++) {
    let maxGrade = 0;
    let prev = elevAt(t.s[n - 1] - L, scale);
    for (let i = 0; i < n; i++) {
      const h = elevAt(t.s[i], scale);
      const g = (h - prev) / ds;
      if (g > maxGrade) maxGrade = g;
      if (-g > maxGrade) maxGrade = -g;
      prev = h;
    }
    if (maxGrade <= MAX_GRADE[kind]) break;
    scale *= (MAX_GRADE[kind] / maxGrade) * 0.97;
  }
  const elev = new Array<number>(n);
  for (let i = 0; i < n; i++) elev[i] = elevAt(t.s[i], scale);
  return { kind, elev };
}

function arcGap(a: number, b: number, L: number): number {
  let d = Math.abs(a - b) % L;
  if (d > L - d) d = L - d;
  return d;
}

/** Per-sample slope (rise per metre) and vertical curvature (1/m, positive in dips) of a closed profile. */
export function slopeAndCurvature(elev: number[], length: number): { grade: number[]; vcurv: number[] } {
  const n = elev.length;
  const ds = length / n;
  const grade = new Array<number>(n);
  const vcurv = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const a = elev[(i - 1 + n) % n];
    const b = elev[i];
    const c = elev[(i + 1) % n];
    grade[i] = (c - a) / (2 * ds);
    vcurv[i] = (a - 2 * b + c) / (ds * ds);
  }
  return { grade, vcurv };
}

// ---------------------------------------------------------------------------
// Generator v3: base hills plus the day's set piece
// ---------------------------------------------------------------------------

/** A stretch of the lap by arc length; `to` < `from` when it wraps past the line. */
export interface ArcSection {
  name: string;
  from: number;
  to: number;
}

export interface ElevationV3 extends Elevation {
  /** The set piece's own height profile (part of elev), for the dev panel. */
  setPiece: string | null;
}

/**
 * v3 terrain: gentle base hills on most days (never flat that often: this is a
 * cartoon), plus the signature's own shape: the Corkscrew's crest and plunge,
 * a climb to a hairpin, Eau Rouge's valley and wall, a run of jumps, the
 * mountain's long climb and steep dive. Returns null if a set piece would
 * reach the level start/finish stretch.
 */
export function elevationV3(t: ElevationInput, sections: ArcSection[], signature: string, rng: Rng): ElevationV3 | null {
  const n = t.n;
  const L = t.length;
  const find = (name: string): ArcSection | undefined => sections.find((q) => q.name === name);
  const len = (q: ArcSection): number => (((q.to - q.from) % L) + L) % L;
  /** Signed arc offset of s from a, in (-L/2, L/2]. */
  const off = (s: number, a: number): number => {
    let u = (s - a) % L;
    if (u > L / 2) u -= L;
    if (u <= -L / 2) u += L;
    return u;
  };
  const ramp = (u: number, u0: number, u1: number): number => smootherstep((u - u0) / (u1 - u0));
  // The set piece: a height function and the stretch it occupies [from, to] (arc offsets from its anchor).
  let piece: { at: number; lo: number; hi: number; h: (u: number) => number } | null = null;
  const main = sections.find((q) => q.name === signature);
  if (signature === 'corkscrew' && main) {
    const D = rng.range(8, 11);
    const l = len(main);
    // Up a long drag to a blind crest, then over it and down through the left-right.
    piece = { at: main.from, lo: -270, hi: l + 25, h: (u) => D * ramp(u, -270, 0) - D * ramp(u, 35, l + 25) };
  } else if (signature === 'climb' && main) {
    const H = rng.range(8, 10.5);
    const l = len(main);
    const top = Math.max(60, l - 60);
    piece = { at: main.from, lo: 0, hi: l + 300, h: (u) => H * ramp(u, 0, top) - H * ramp(u, l + 20, l + 300) };
  } else if (signature === 'eauRouge' && main) {
    const drop = rng.range(5, 7);
    const rise = rng.range(7, 10);
    const l = len(main);
    const bottom = l * 0.25;
    piece = {
      at: main.from,
      lo: -130,
      hi: l + 320,
      h: (u) => -drop * ramp(u, -130, bottom) + (drop + rise) * ramp(u, bottom, l + 5) - rise * ramp(u, l + 50, l + 320),
    };
  } else if (signature === 'jumps' && main) {
    const l = len(main);
    const count = l > 150 ? 3 : 2;
    // Big enough to throw the car light over every one (a cartoon, not a road).
    const H = rng.range(3.0, 3.8);
    const w = Math.max(rng.range(26, 30), (1.717 * H) / 0.21);
    const centres: number[] = [];
    for (let k = 0; k < count; k++) centres.push(28 + ((l - 56) * (k + 0.5)) / count);
    piece = {
      at: main.from,
      lo: 0,
      hi: l,
      h: (u) => {
        let h = 0;
        for (const c of centres) h += H * bump((u - c) / w);
        return h;
      },
    };
  } else if (signature === 'mountain') {
    const up = find('mountainUp');
    const down = find('mountainDown');
    if (up && down) {
      const H = rng.range(13, 17);
      const ld = off(down.from, up.from) + len(down);
      const crest = off(down.from, up.from);
      piece = { at: up.from, lo: -70, hi: ld + 45, h: (u) => H * ramp(u, -70, crest - 5) - H * ramp(u, crest, ld + 45) };
    }
  }
  // Clear of the level start/finish stretch.
  if (piece) {
    const a = piece.at;
    for (const [lo, hi] of [
      [piece.lo, piece.hi],
    ]) {
      for (let u = lo; u <= hi; u += 5) {
        const s = (((a + u) % L) + L) % L;
        const fromLine = Math.min(s, L - s);
        if ((s < L / 2 && s < FLAT_AFTER) || (s >= L / 2 && L - s < FLAT_BEFORE) || fromLine < 60) return null;
      }
    }
  }
  // Reserved from the base hills: the set piece, and banked or walled stretches (they stay level).
  const reserved: { at: number; half: number }[] = [];
  if (piece) reserved.push({ at: (((piece.at + (piece.lo + piece.hi) / 2) % L) + L) % L, half: (piece.hi - piece.lo) / 2 + 30 });
  for (const q of sections) {
    if (q.name !== 'banking' && q.name !== 'karussell' && q.name !== 'streets') continue;
    const l = len(q);
    reserved.push({ at: (q.from + l / 2) % L, half: l / 2 + 40 });
  }
  for (const r of t.reserved ?? []) reserved.push(r);

  // Base hills: rolling or hilly most days, now and then flat.
  const r = rng.next();
  const kind: TerrainKind = piece ? (r < 0.5 ? 'rolling' : 'flat') : r < 0.22 ? 'flat' : r < 0.62 ? 'rolling' : 'hilly';
  const hills: Hill[] = [];
  const lo = FLAT_AFTER;
  const hi = L - FLAT_BEFORE;
  if (kind !== 'flat') {
    const count = kind === 'rolling' ? rng.int(2, 3) : rng.int(3, 5);
    const hMin = kind === 'hilly' ? 4 : 2;
    const hMax = kind === 'hilly' ? 11 : 5.5;
    for (let k = 0, tries = 0; k < count && tries < 50; tries++) {
      const w = rng.range(160, 320);
      if (hi - lo < 2 * w) continue;
      const at = rng.range(lo + w, hi - w);
      if (reserved.some((q) => arcGap(at, q.at, L) < w + q.half)) continue;
      hills.push({ at, height: (rng.chance(0.5) ? 1 : -1) * rng.range(hMin, hMax), w });
      k++;
    }
  }
  const base = (s: number, scale: number): number => {
    let h = 0;
    for (const p of hills) h += scale * p.height * bump((s - p.at) / p.w);
    return h;
  };
  // Tame the base hills if overlapping ones got steeper than the day allows.
  const ds = L / n;
  const maxGrade = kind === 'hilly' ? 0.09 : 0.055;
  let scale = 1;
  for (let pass = 0; pass < 6 && hills.length; pass++) {
    let steepest = 0;
    let prev = base(t.s[n - 1] - L, scale);
    for (let i = 0; i < n; i++) {
      const h = base(t.s[i], scale);
      steepest = Math.max(steepest, Math.abs(h - prev) / ds);
      prev = h;
    }
    if (steepest <= maxGrade) break;
    scale *= (maxGrade / steepest) * 0.97;
  }
  const elev = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    let h = base(t.s[i], scale);
    if (piece) {
      const u = off(t.s[i], piece.at);
      if (u >= piece.lo - 1 && u <= piece.hi + 1) h += piece.h(u);
    }
    elev[i] = h;
  }
  return { kind, elev, setPiece: piece ? signature : null };
}
